import {
  useGetExternalAccountsSuspense,
  useGetTransactions,
} from 'api/payments/generated/fragmentPaymentsAPI';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { makePath } from 'utils/routes';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';
import type { ColumnDefinition } from '@fragment/ui/src/components/Table';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';

import { CreateExternalAccountModal } from './CreateExternalAccountModal';
import { FormatBillingAmount } from './formatBillingAmount';

type AccountRow = {
  id: string;
  external_id: string;
  name: string;
};

type TransactionRow = {
  id: string;
  external_id: string;
  amount: string;
  currency: string;
  posted: string;
  unallocated_amount: string;
  account_name: string;
};

/** Small triangle badge rendered next to account names */
const TriangleBadge = ({ size = 14 }: { size?: number }) => {
  const points = `${size / 2},1 ${size - 1},${size - 1} 1,${size - 1}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0"
    >
      <polygon
        points={points}
        fill="rgb(var(--colour-main-500))"
        stroke="rgb(var(--colour-main-500))"
        strokeWidth={1}
        strokeLinejoin="round"
      />
    </svg>
  );
};

const accountSchema: ColumnDefinition<AccountRow>[] = [
  {
    title: 'Account',
    colspan: 'col-span-4',
    format: (row: AccountRow) => (
      <span className="flex items-center gap-x-[6px]">
        <TriangleBadge />
        <span>{row.name}</span>
      </span>
    ),
  },
  {
    title: 'External ID',
    key: 'external_id',
    justify: 'right',
    colspan: 'col-span-2',
  },
];

const transactionSchema: ColumnDefinition<TransactionRow>[] = [
  {
    title: 'External ID',
    key: 'external_id',
    colspan: 'col-span-2',
  },
  {
    title: 'Account',
    key: 'account_name',
    colspan: 'col-span-2',
  },
  {
    title: 'Posted',
    colspan: 'col-span-1',
    format: (row: TransactionRow) => (
      <span>{new Date(row.posted).toLocaleDateString()}</span>
    ),
  },
  {
    title: 'Unallocated',
    justify: 'right',
    colspan: 'col-span-1',
    format: (row: TransactionRow) => {
      const code = row.currency as CurrencyCode;
      const precision = currencyPrecisionMap[code] || 2;
      return (
        <FormatBillingAmount
          amount={Math.abs(Number(row.unallocated_amount))}
          currencyCode={code}
          currencyPrecision={precision}
        />
      );
    },
  },
];

const AccountColumnHeaders = () => (
  <div className="grid grid-flow-col auto-cols-fr gap-x-[1ch] px-f1 items-center h-f4 text-xmd text-main-500 border-b-[0.5px] border-main-200">
    <div className="col-span-4">Account</div>
    <div className="col-span-2 text-right">External ID</div>
  </div>
);

const TransactionColumnHeaders = () => (
  <div className="grid grid-flow-col auto-cols-fr gap-x-[1ch] px-f1 items-center h-f4 text-xmd text-main-500 border-b-[0.5px] border-main-200">
    <div className="col-span-2">External ID</div>
    <div className="col-span-2">Account</div>
    <div className="col-span-1">Posted</div>
    <div className="col-span-1 text-right">Unallocated</div>
  </div>
);

const accountEmptyValue = {
  Account: 'No Accounts Yet',
  'External ID': 'N/A',
};

const transactionEmptyValue = {
  'External ID': 'No Transactions',
  Account: 'N/A',
  Posted: 'N/A',
  Unallocated: 'N/A',
};

const SKELETON_MAX_ROWS = 10;

type Props = {
  workspaceId: string;
};

const TransactionListPageLoading = ({ workspaceId: _workspaceId }: Props) => {
  const [visibleRows, setVisibleRows] = useState(0);
  const directionRef = useRef(1);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleRows((prev) => {
        const next = prev + directionRef.current;
        if (next >= SKELETON_MAX_ROWS) {
          directionRef.current = -1;
          return SKELETON_MAX_ROWS;
        }
        if (next <= 0) {
          directionRef.current = 1;
          return 0;
        }
        return next;
      });
    }, 120);
    return () => clearInterval(timer);
  }, []);

  return (
    <PaymentViewLayout
      title="Reconciliation"
      primaryAction={{
        title: 'Add Account',
        onClick: () => {},
        disabled: true,
      }}
    >
      <div className="flex flex-col w-full text-xmd font-helvetica">
        {/* Header section — 160px */}
        <div className="flex flex-col px-f1 pt-f1 h-[160px] border-b-[0.5px] border-main-300">
          <span className="text-xmd text-main-500">Unreconciled</span>
          <div className="text-lg text-main-500 pt-f1">—</div>
        </div>
        <div className="sticky top-0 z-10">
          <TransactionColumnHeaders />
        </div>
        {/* eslint-disable react/no-array-index-key */}
        {Array.from({ length: visibleRows }).map((__, i) => (
          <div
            key={`skeleton-row-${i}`}
            className="h-[40px] border-b-[0.5px] border-main-200"
          />
        ))}
        {/* eslint-enable react/no-array-index-key */}
      </div>
    </PaymentViewLayout>
  );
};

const TransactionListPageContent = ({ workspaceId }: Props): JSX.Element => {
  useDocumentTitle('Reconciliation');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const { data: accountsData } = useGetExternalAccountsSuspense();
  const { data: unreconciledData } = useGetTransactions({
    reconciliation_status: 'unreconciled',
  });

  const accounts: AccountRow[] = accountsData.data;

  /** Build a lookup from account id to name */
  const accountNameById = useMemo(() => {
    const map = new Map<string, string>();
    accounts.forEach((acc) => map.set(acc.id, acc.name));
    return map;
  }, [accounts]);

  /** Sum unreconciled amounts grouped by currency */
  const unreconciledTotals = useMemo(() => {
    const txns = unreconciledData?.data ?? [];
    const byCurrency = new Map<string, number>();
    txns.forEach((txn) => {
      const amt = Math.abs(Number(txn.unallocated_amount));
      byCurrency.set(txn.currency, (byCurrency.get(txn.currency) ?? 0) + amt);
    });
    return Array.from(byCurrency.entries()).map(([currency, amount]) => ({
      currency,
      amount,
    }));
  }, [unreconciledData]);

  /** All unreconciled transactions as table rows */
  const transactionRows: TransactionRow[] = useMemo(() => {
    const txns = unreconciledData?.data ?? [];
    return txns.map((txn) => ({
      id: txn.id,
      external_id: txn.external_id,
      amount: txn.amount,
      currency: txn.currency,
      posted: txn.posted,
      unallocated_amount: txn.unallocated_amount,
      account_name: accountNameById.get(txn.account.id) ?? txn.account.id,
    }));
  }, [unreconciledData, accountNameById]);

  const getAccountPath = useCallback(
    (account: AccountRow) =>
      makePath(['w', workspaceId, 'payments', 'accounts', account.id], {
        absolute: true,
      }),
    [workspaceId]
  );

  const getTransactionPath = useCallback(
    (txn: TransactionRow) =>
      makePath(['w', workspaceId, 'payments', 'transactions', txn.id], {
        absolute: true,
      }),
    [workspaceId]
  );

  const handleCreate = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  return (
    <>
      <PaymentViewLayout
        title="Reconciliation"
        primaryAction={{
          title: 'Add Account',
          onClick: handleCreate,
          'data-testid': 'create-external-account-button',
        }}
      >
        <div className="flex flex-col min-h-f1 w-full h-full text-xmd font-helvetica">
          {/* Header section — 160px, "Unreconciled" above balances */}
          <div className="flex flex-col px-f1 pt-f1 h-[160px] border-b-[0.5px] border-main-300">
            <span className="text-xmd text-main-500">Unreconciled</span>
            <div className="text-lg text-main pt-f1">
              {unreconciledTotals.length > 0 ? (
                <span className="flex flex-col">
                  {unreconciledTotals.map(({ currency, amount }) => (
                    <FormatBillingAmount
                      key={currency}
                      amount={amount}
                      currencyCode={
                        currency as Parameters<
                          typeof FormatBillingAmount
                        >[0]['currencyCode']
                      }
                      className="text-lg"
                    />
                  ))}
                </span>
              ) : (
                <span className="text-main-500">—</span>
              )}
            </div>
          </div>

          {/* Transactions table */}
          <div className="sticky top-0 z-10">
            <TransactionColumnHeaders />
          </div>
          <NavTableV2
            textSize="xmd"
            data={transactionRows}
            emptyValue={transactionEmptyValue}
            getPath={getTransactionPath}
            schema={transactionSchema}
            showHeader={false}
            stickyHeader={false}
            rowHeight="fixed"
          />

          {/* Spacer — 2 row heights */}
          <div className="h-f8" />

          {/* Accounts table */}
          <div className="sticky top-0 z-10">
            <AccountColumnHeaders />
          </div>
          <NavTableV2
            textSize="xmd"
            data={accounts}
            emptyValue={accountEmptyValue}
            getPath={getAccountPath}
            schema={accountSchema}
            showHeader={false}
            stickyHeader={false}
            rowHeight="fixed"
          />
        </div>
      </PaymentViewLayout>
      {showCreateModal && (
        <CreateExternalAccountModal onClose={handleCloseModal} />
      )}
    </>
  );
};

export const TransactionListPage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense fallback={<TransactionListPageLoading workspaceId={workspaceId} />}>
    <TransactionListPageContent workspaceId={workspaceId} />
  </Suspense>
);
